import PaymentFormContainer from '@/components/billing/PaymentFormContainer';
import { PromotionCodeRow } from '@/components/billing/PromotionCodeRow';
import { useJustAddedPaymentMethod } from '@/components/billing/useJustAddedPaymentMethod';
import { FlexRow } from '@/components/common/Flex';
import MembershipPriceDetails from '@/components/common/MembershipPriceDetails';
import PlentyFeatureList from '@/components/common/PlentyFeatureList';
import { HeroText, Paragraph, Subheader } from '@/components/common/Typography';
import PlentyFeatureListBudgeting from '@/components/onboarding/experiments/budgeting/PlentyFeatureListBudgeting';
import { getBillStr } from '@/services/billing';
import { useIsOnboardingExperimentEnabled } from '@/services/experiments/useIsOnboardingExperimentEnabled';
import { useUserData } from '@/services/user';
import { toDollarsShort } from '@/util/currency';
import { Box, Divider, Text } from '@chakra-ui/react';
import { PromotionCodeFieldsFragment } from '@common/graphql/generated';
import { ANNUAL_MEMBERSHIP_PRICE, FREE_TRIAL_DAYS } from '@common/memberships';
import { getPartnerName } from '@common/react/user';
import { useState } from 'react';
import { FlexCol } from '../common/Flex';

interface PaymentViewProps {
    includeTrial: boolean;
    onComplete?: () => void;
}

const PaymentView = ({ includeTrial, onComplete }: PaymentViewProps) => {
    const { activePartnershipId, membership, partnerInfo } = useUserData();
    const [appliedPromotionCode, setAppliedPromotionCode] =
        useState<PromotionCodeFieldsFragment | null>(null);
    const [, setJustAddedPaymentMethod] = useJustAddedPaymentMethod();
    const isBudgetingExperiment = useIsOnboardingExperimentEnabled('budgeting');

    const subtitle = membership
        ? includeTrial
            ? getBillStr(membership)
            : 'Billed annually to you starting today'
        : `Billed annually to you starting in ${FREE_TRIAL_DAYS} days`;
    const hasPartner = !!activePartnershipId;
    const membershipPrice = membership?.amount || ANNUAL_MEMBERSHIP_PRICE;

    return (
        <FlexRow
            justify="space-between"
            h="full"
            direction={{ base: 'column', md: 'row' }}
        >
            <FlexCol
                flex={1.5}
                backgroundImage="url(/images/mountains.png)"
                backgroundPosition="bottom"
                backgroundRepeat="no-repeat"
                backgroundSize={{ base: 'cover', md: 'contain' }}
                borderRight="1px solid"
                borderColor="gray.200"
            >
                <FlexCol p={10}>
                    <FlexCol>
                        <HeroText
                            fontFamily="serif"
                            lineHeight={1}
                            fontSize="6xl"
                            mb={4}
                        >
                            {includeTrial ? (
                                <>
                                    Your first month
                                    <br /> is{' '}
                                    <Text as="span" color="blue.600">
                                        free.
                                    </Text>
                                </>
                            ) : (
                                <>
                                    Resume your
                                    <br />
                                    membership
                                </>
                            )}
                        </HeroText>
                        <Paragraph fontSize="md" color="textMuted" mb={4}>
                            {includeTrial ? 'Then become' : 'Become'} Plenty
                            members for just{' '}
                            {toDollarsShort(Math.round(membershipPrice / 12))}
                            /month.
                        </Paragraph>
                    </FlexCol>
                    <Divider />
                    <FlexCol
                        py={4}
                        borderBottom="1px solid transparent"
                        borderColor={hasPartner ? undefined : 'gray.400'}
                        transition="all 200ms ease-out"
                    >
                        <MembershipPriceDetails
                            title="Annual membership"
                            subtitle={subtitle}
                            price={membershipPrice}
                            promotionCode={appliedPromotionCode}
                        />
                        <Box
                            h={hasPartner ? 'auto' : 0}
                            transition="all 400ms ease-in-out"
                            transitionDelay={`${hasPartner ? 0 : 200}ms`}
                        >
                            <Box
                                transition={`all 400ms ease-in-out`}
                                transitionDelay={`${hasPartner ? 200 : 0}ms`}
                                opacity={hasPartner ? 1 : 0}
                                visibility={hasPartner ? 'visible' : 'hidden'}
                                pointerEvents={hasPartner ? 'all' : 'none'}
                            >
                                <Divider mt={4} />
                            </Box>
                        </Box>
                        {(!membership || membership.isMine) && (
                            <PromotionCodeRow
                                appliedPromotionCode={appliedPromotionCode}
                                setAppliedPromotionCode={
                                    setAppliedPromotionCode
                                }
                            />
                        )}
                        {isBudgetingExperiment ? (
                            <PlentyFeatureListBudgeting />
                        ) : (
                            <PlentyFeatureList />
                        )}
                    </FlexCol>
                </FlexCol>
            </FlexCol>
            {!membership || membership.isMine ? (
                <FlexCol flex={1} p={10} bg="gray.100">
                    <Subheader mb={4}>Billing details:</Subheader>
                    <PaymentFormContainer
                        currentMembershipId={
                            membership
                                ? membership.isEnded
                                    ? undefined
                                    : membership.id
                                : undefined
                        }
                        buttonText="Start your membership"
                        onComplete={() => {
                            // We need to use sessionStorage for this because other parts of the app need to react immediately to this
                            // We can't rely on the user's `membership` updating immediately because the
                            // payment method is added to the membership via an async Stripe webhook
                            setJustAddedPaymentMethod(true);

                            if (appliedPromotionCode) {
                                setAppliedPromotionCode(null);
                            }
                            onComplete?.();
                        }}
                        promotionCode={appliedPromotionCode}
                    />
                </FlexCol>
            ) : (
                <Paragraph flex={1} color="textMuted">
                    At this time, only{' '}
                    {getPartnerName(partnerInfo, 'your partner')} can add a
                    payment method. We are working hard to provide more
                    flexibility soon.
                </Paragraph>
            )}
        </FlexRow>
    );
};

export default PaymentView;
